const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

const request = async (path, options = {}) => {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: getHeaders()
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Request failed');
  return data;
};

export const authService = {
  register: (body) => request('/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (body) => request('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  getMe: () => request('/auth/me'),
  setPassword: (body) => request('/auth/set-password', { method: 'POST', body: JSON.stringify(body) }),
  forgotPassword: (body) => request('/auth/forgot-password', { method: 'POST', body: JSON.stringify(body) })
};

// Uploads a post with an attached file (any format), reporting progress from 0 to 100
const uploadPostWithFile = (fields, file, onProgress) =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    Object.entries(fields).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE_URL}/posts`);
    xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* not JSON */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data.message || 'Upload failed'));
    };
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection.'));
    xhr.send(form);
  });

export const postService = {
  getAll: (type) => request(`/posts${type ? `?type=${type}` : ''}`),
  create: (body) => request('/posts', { method: 'POST', body: JSON.stringify(body) }),
  createWithFile: uploadPostWithFile,
  // Short-lived pass for one file; used in <video>/<img> links instead of the login token
  fileToken: (postId) => request(`/posts/${postId}/file-token`).then((d) => d.token),
  fileSrc: (file, pass) => `${BASE_URL}/posts/file/${file}?ft=${pass}`,
  // Raw bytes for the in-site PDF viewer (sent with the login header, never as a link)
  fileBytes: async (file) => {
    const res = await fetch(`${BASE_URL}/posts/file/${file}`, { headers: getHeaders() });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Could not load file');
    }
    return new Uint8Array(await res.arrayBuffer());
  },
  delete: (id) => request(`/posts/${id}`, { method: 'DELETE' })
};

export const queryService = {
  getAll: () => request('/queries'),
  create: (body) => request('/queries', { method: 'POST', body: JSON.stringify(body) }),
  answer: (id, answer) =>
    request(`/queries/${id}/answer`, { method: 'PUT', body: JSON.stringify({ answer }) })
};

export const meetingService = {
  getAll: (type) => request(`/meetings${type ? `?type=${type}` : ''}`),
  create: (body) => request('/meetings', { method: 'POST', body: JSON.stringify(body) }),
  join: (id) => request(`/meetings/${id}/join`, { method: 'POST' }),
  delete: (id) => request(`/meetings/${id}`, { method: 'DELETE' })
};

export const userService = {
  getAll: () => request('/users'),
  addTeacher: (body) => request('/users/teachers', { method: 'POST', body: JSON.stringify(body) }),
  addStudent: (body) => request('/users/students', { method: 'POST', body: JSON.stringify(body) }),
  resendInvite: (id) => request(`/users/${id}/resend-invite`, { method: 'POST' }),
  emailStatus: () => request('/users/email-status'),
  delete: (id) => request(`/users/${id}`, { method: 'DELETE' }),
  backupUrl: () => `${BASE_URL}/users/backup?token=${localStorage.getItem('token')}`,
  updateProfile: (body) => request('/users/profile', { method: 'PUT', body: JSON.stringify(body) })
};
